import { ErrorState } from "$components/error-state";
import { Table, type TableColumnDefinition } from "$components/table";
import { formatDate, formatUserName } from "$routes/inventory/_lib/formatting";
import StatusChip from "$routes/inventory/_lib/status-chip";
import UserName from "$routes/inventory/_lib/user-name";
import { gql } from "@apollo/client";
import type * as Types from "elephant-web/gql";
import type { InventoryMoveRequestStatus } from "elephant-web/gql";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useInventoryMoveRequestsListQuery } from "./__generated__/move-requests-table.gql";

gql`
  query inventoryMoveRequestsList(
    $input: InventoryMoveRequestsInput!
    $options: InventoryMoveRequestsOptions
  ) {
    inventoryMoveRequests(input: $input, options: $options) {
      totalCount
      items {
        id
        createdAt
        status
        receivingLocation {
          name
        }
        requestor {
          person {
            firstname
            lastname
          }
        }
        actionedAt
        actionerNotes
      }
    }
  }
`;

interface Props {
  receivingLocationId?: string | null;
  status: InventoryMoveRequestStatus | null;
  onRowClick?: (row: {
    id: string;
    status: Types.InventoryMoveRequestStatus;
  }) => void;

  pageSize?: number;
}

const MoveRequestsTable: FC<Props> = ({
  receivingLocationId,
  onRowClick,
  status,
  pageSize = 20,
}) => {
  const { formatMessage } = useIntl();

  const tableColumns: TableColumnDefinition[] = [
    {
      field: "createdAt",
      headerName: formatMessage({
        id: "inventory.moveRequests.table.columnLabel.createdAt",
        defaultMessage: "Date Requested",
      }),
      flex: 1,
    },
    {
      field: "locationName",
      headerName: formatMessage({
        id: "inventory.moveRequests.table.columnLabel.locationName",
        defaultMessage: "Location",
      }),
      flex: 2,
    },
    {
      field: "requestedBy",
      headerName: formatMessage({
        id: "inventory.moveRequests.table.columnLabel.requestedBy",
        defaultMessage: "Requested By",
      }),
      flex: 2,
      renderCell: (val) => <UserName name={val} />,
    },
    {
      field: "status",
      headerName: formatMessage({
        id: "inventory.moveRequests.table.columnLabel.status",
        defaultMessage: "Status",
      }),
      flex: 1,
      renderCell: (_status) => <StatusChip status={_status} />,
    },
    {
      field: "actionedAt",
      headerName: formatMessage({
        id: "inventory.moveRequests.table.columnLabel.actionedAt",
        defaultMessage: "Date Processed",
      }),
      flex: 1,
    },
    {
      field: "actionerNotes",
      headerName: formatMessage({
        id: "inventory.moveRequests.table.columnLabel.actionerNotes",
        defaultMessage: "Notes",
      }),
      flex: 3,
      renderCell: (notes) => (
        <div
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            maxWidth: "512px",
          }}
        >
          {notes}
        </div>
      ),
    },
  ];

  const [currentPage, setCurrentPage] = useState(0);

  const { data, loading, error } = useInventoryMoveRequestsListQuery({
    variables: {
      input: {
        receivingLocationId,
        status,
      },
      options: {
        pageNumber: currentPage,
        pageSize,
      },
    },
    fetchPolicy: "cache-and-network",
    pollInterval: 30 * 1000,
  });

  if (error) {
    return <ErrorState variant="inline" error={error} />;
  }

  const mappedData = data?.inventoryMoveRequests?.items?.map((item) => {
    return {
      id: item.id,
      createdAt: formatDate(item.createdAt),
      locationName: item.receivingLocation.name,
      requestedBy: formatUserName(item.requestor),
      status: item.status,
      actionedAt: formatDate(item.actionedAt),
      actionerNotes: item.actionerNotes,
    };
  });

  return (
    <Table
      loading={loading}
      columns={tableColumns}
      rows={mappedData ?? []}
      noDataMessage={formatMessage({
        id: "inventory.moveRequests.list.noData",
        defaultMessage: "No requests found.",
      })}
      onRowClick={(row) =>
        onRowClick({ id: row.id as string, status: row.status })
      }
      pagination={{
        page: currentPage,
        pageSize,
        totalRows: data?.inventoryMoveRequests?.totalCount,
        handlePageChange: (newPageNumber) => {
          setCurrentPage(newPageNumber);
        },
      }}
    />
  );
};

export default MoveRequestsTable;
